import { useEffect, useRef, useState } from 'react';
import { tmdb, posterUrl, yearFromReleaseDate } from '../lib/tmdb.js';
import services from '../config/services.json';
import MovieModal from './MovieModal.jsx';

// Header search bar with a debounced live-results dropdown.
// Returns matches by movie title AND by person (actor / director), with
// availability badges on every film.
export default function SearchBar() {
  const [q, setQ]         = useState('');
  const [movies, setMovies] = useState([]);
  const [tvShows, setTvShows] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]   = useState(false);
  const [openRef, setOpenRef] = useState(null);
  const wrapRef = useRef(null);

  const providerIds = services.services
    .filter((s) => s.active)
    .map((s) => s.tmdbProviderId)
    .join('|');

  useEffect(() => {
    if (!q.trim()) {
      setMovies([]); setTvShows([]); setPeople([]); setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(() => {
      tmdb.search({ q: q.trim(), providers: providerIds })
        .then((data) => {
          setMovies(data.movies || []);
          setTvShows(data.tv || []);
          setPeople(data.people || []);
        })
        .catch(() => { setMovies([]); setTvShows([]); setPeople([]); })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [q, providerIds]);

  useEffect(() => {
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function pickMovie(m) {
    setOpenRef({ tmdb_id: m.id, media_type: m.media_type || 'movie' });
    setOpen(false);
    setQ('');
  }

  const nothing = !loading && movies.length === 0 && tvShows.length === 0 &&
    people.every((p) => p.asActor.length === 0 && p.asDirector.length === 0);

  return (
    <>
      <div className="hb-search" ref={wrapRef}>
        <input
          type="search"
          className="hb-search__input"
          placeholder="Search titles, actors, directors…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          aria-label="Search"
        />
        {open && q.trim() && (
          <div className="hb-search__dropdown" role="listbox">
            {loading && <div className="hb-search__hint">Searching…</div>}

            {nothing && <div className="hb-search__hint">No matches for “{q}.”</div>}

            {/* Movie title matches */}
            {movies.length > 0 && (
              <>
                <div className="hb-search__section">Movies</div>
                {movies.map((m) => (
                  <MovieRow key={'m' + m.id} movie={m} onClick={() => pickMovie(m)} />
                ))}
              </>
            )}

            {/* TV show title matches */}
            {tvShows.length > 0 && (
              <>
                <div className="hb-search__section">TV Shows</div>
                {tvShows.map((m) => (
                  <MovieRow key={'t' + m.id} movie={m} onClick={() => pickMovie(m)} />
                ))}
              </>
            )}

            {/* Person matches: starring + directed */}
            {people.map((p) => {
              if (p.asActor.length === 0 && p.asDirector.length === 0) return null;
              return (
                <div key={'p' + p.id}>
                  {p.asActor.length > 0 && (
                    <>
                      <div className="hb-search__section">Starring {p.name}</div>
                      {p.asActor.map((m) => (
                        <MovieRow key={'a' + p.id + '-' + m.id} movie={m} onClick={() => pickMovie(m)} />
                      ))}
                    </>
                  )}
                  {p.asDirector.length > 0 && (
                    <>
                      <div className="hb-search__section">Directed by {p.name}</div>
                      {p.asDirector.map((m) => (
                        <MovieRow key={'d' + p.id + '-' + m.id} movie={m} onClick={() => pickMovie(m)} />
                      ))}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {openRef && <MovieModal movieId={openRef} onClose={() => setOpenRef(null)} />}
    </>
  );
}

function MovieRow({ movie, onClick }) {
  const on   = movie._onMyServices || [];
  const rent = movie._rentOn || [];
  const onMine = on.length > 0;
  const title = movie.title || movie.name || '';
  const isTv = movie.media_type === 'tv';

  return (
    <button
      type="button"
      className={'hb-search__result' + (onMine ? '' : ' is-dim')}
      onClick={onClick}
      role="option"
    >
      <img
        className="hb-search__thumb"
        src={posterUrl(movie.poster_path, 'w92')}
        alt=""
        loading="lazy"
      />
      <div className="hb-search__meta">
        <div className="hb-search__title">
          {title}
          {isTv && <span className="hb-search__tv-tag">TV</span>}
        </div>
        <div className="hb-search__sub">{yearFromReleaseDate(movie.release_date || movie.first_air_date) || ''}</div>
        <div className="hb-search__badges">
          {onMine ? (
            on.map((p) => {
              const svc = services.services.find((s) => s.tmdbProviderId === p.provider_id);
              return (
                <span
                  key={p.provider_id}
                  className="hb-search__badge"
                  style={{
                    background: svc?.color || '#1B3F8B',
                    color: svc ? pickTextColor(svc.color) : '#fff',
                  }}
                >
                  {svc?.name || p.provider_name}
                </span>
              );
            })
          ) : rent.length ? (
            <span className="hb-search__badge hb-search__badge--rent">
              Rent on {rent.map((p) => p.provider_name).join(', ')}
            </span>
          ) : (
            <span className="hb-search__badge hb-search__badge--none">
              Not currently available
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function pickTextColor(hex) {
  const c = String(hex || '#1B3F8B').replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#1B3F8B' : '#FFFFFF';
}
